<script setup lang="ts">
import { IonButton, IonModal } from '@ionic/vue'
import { Share } from '@capacitor/share'
import { Capacitor } from '@capacitor/core'
import { Clipboard } from '@capacitor/clipboard'
import { ref } from 'vue'
import CloseIcon from './svg/CloseIcon.vue'
import ShareIcon from '@/components/svg/ShareIcon.vue'
import LinkIcon from '@/components/svg/LinkIcon.vue'
import type { Chat } from '@/types'
import { useToastStore } from '@/store/toast'
import VideoShareCover from '@/assets/video_share_cover.png'

interface Props {
  chat: Chat
}

interface Emits {
  (e: 'onCreateVideo'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const toastStore = useToastStore()
const showShareSheet = ref(false)

function showShare() {
  showShareSheet.value = true
}

async function shareContent() {
  if (Capacitor.getPlatform() !== 'web') {
    Share.share({
      title: 'Share the Sekai',
      url: `https://app.sekai.chat/sekai/${
        props.chat.universe.universeId
      }`,
      text: `${props.chat.universe.universeName.slice(0, 100)}...`,
      dialogTitle: 'Share with friends',
    })
  }
  else {
    await Clipboard.write({
      string: `https://app.sekai.chat/sekai/${
        props.chat.universe.universeId
      }`,
    })
    toastStore.setToast('Sekai link is copied to clipboard')
    showShareSheet.value = false
  }
}

function dismissSheet() {
  showShareSheet.value = false
}

defineExpose({
  dismissSheet,
})
</script>

<template>
  <ion-button size="small" fill="clear" @pointerdown="showShare">
    <share-icon />
  </ion-button>
  <ion-modal
    id="share-modal"
    :is-open="showShareSheet"
    style="--backdrop-opacity: 0.5"
    @did-dismiss="
      () => {
        showShareSheet = false
      }
    "
    @ion-modal-did-dismiss="
      () => {
        showShareSheet = false
      }
    "
  >
    <div
      class="flex flex-col gap-2 justify-end items-center pb-unsafe-area bg-transparent full-height"
      @pointerdown="() => {
        showShareSheet = false
      }"
    >
      <div
        class="flex justify-center items-center p-4 w-full opacity-bg rd-4 gap-2" @pointerdown="(e) => {
          e.preventDefault()
          e.stopPropagation()
          shareContent()
        }"
      >
        <link-icon />
        <p>Copy link</p>
      </div>
      <div
        class="flex flex-col gap-4 items-center w-full opacity-bg p-4 rd-4" @pointerdown="(e) => {
          e.preventDefault()
          showShareSheet = false
          emit('onCreateVideo')
        }"
      >
        <img :src="VideoShareCover">
        <p class="text-4 leading-normal color-#fff fw-700">
          Video Share
        </p>
      </div>
      <ion-button
        fill="clear"
        @pointerdown="showShareSheet = false"
      >
        <close-icon />
      </ion-button>
    </div>
  </ion-modal>
</template>

<style scoped>
.full-height {
  height: 100vh;

}

ion-modal#share-modal {
  padding-left: 16px;
  padding-right: 16px;
  --min-width: 250px;
  --height: fit-content;
  margin-top: auto;
  --background: transparent;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}
ion-modal > .ion-page {
  justify-content: flex-end;
}

ion-modal#share-modal .wrapper {
  margin-bottom: 10px;
}

.opacity-bg {
  background: rgba(0, 0, 0, 0.25);
}
</style>
